import { LoggerFactory, Schema } from "lego-gui-lib";
import { action, observable } from "mobx";
import { ReactText } from "react";
import { v4 as uuidv4 } from "uuid";
import { LocalOutput, TreeBlockConfigurations } from "./schema";
import { showErrorMessage } from "./schema/validators";
import { TreeBlockDataSource } from "./TreeBlockDataSource";
import { TreeBlockType } from "./TreeBlockType";

export enum OutputType {
  AREA_GROUP = "AREA_GROUP",
  CALENDAR_DAY_TYPE = "CALENDAR_DAY_TYPE",
  DEFAULT = "DEFAULT",
  LIST = "LIST",
  MATCHED = "MATCHED",
  RANGE = "RANGE",
  SIMPLE = "SIMPLE",
  TIMEOUT = "TIMEOUT",
  ERROR = "ERROR",
  TIMESLOT = "TIMESLOT",
  NETWORK_OPERATOR = "NETWORK_OPERATOR"
}

export type RemoteOutput = {
  id: ReactText;
  output: any;
  type: OutputType;
};

export interface BlockObject {
  configuredOutputs: RemoteOutput[];
  destinationRef: number;
  fkDto: { name: string };
  id: string;
  leaf: boolean;
  meta: { info: string[]; warning: string[] };
  treeRef: number;
  type: TreeBlockType;
  counterName?: string;
  counterValue?: string;
  releaseReason?: string;
  releaseResponseCode?: string;
}

export type TreeBlockOutputs = {
  [k: string]: LocalOutput[];
};

export class TreeBlockOutputsDataSource {
  @observable public outputs: TreeBlockOutputs;
  @observable public outputOptions: TreeBlockOutputs;
  @observable public formOutputType: OutputType | null;
  private treeBlockStore: TreeBlockDataSource<any, string>;
  @observable private treeBlockConfigurations: TreeBlockConfigurations;
  private log = LoggerFactory.create("TreeBlockOutputsDataSource");

  public constructor(
    treeBlockStore: TreeBlockDataSource<any, string>,
    treeBlockConfigurations: TreeBlockConfigurations
  ) {
    this.outputs = {};
    this.outputOptions = {};
    this.treeBlockStore = treeBlockStore;
    this.treeBlockConfigurations = treeBlockConfigurations;
    this.formOutputType = null;
  }

  public findBlockById(treeBlockId: ReactText): BlockObject {
    return this.treeBlockStore.treeStructure.blocks.find(
      (block: BlockObject) => block.id === treeBlockId
    ) as BlockObject;
  }

  @action
  public initOutputOptions(
    treeBlockId: ReactText,
    tBlockType: TreeBlockType
  ): void {
    if (!this.outputOptions[treeBlockId]) {
      let options: LocalOutput[] = [];
      // static output options
      if (this.treeBlockConfigurations[tBlockType].outputOptions) {
        // @ts-ignore
        options = [...this.treeBlockConfigurations[tBlockType].outputOptions];
      }
      // dynamic output options
      this.outputs[treeBlockId]?.forEach((output: LocalOutput) => {
        if (
          options.findIndex(option => option.optionId === output.optionId) < 0
        ) {
          output.isDynamicOption = true;
          output.optionId = output.id;
          options.push(output);
        }
      });
      this.outputOptions[treeBlockId] = options;
    }
  }

  @action
  prepareOutputValue(value: string | number, blockType: TreeBlockType) {
    if (
      blockType === TreeBlockType.TRAFFIC_DISTRIBUTION &&
      value != "DEFAULT" &&
      value.toString().length < 3
    ) {
      value = value + "_" + uuidv4();
    }
    return value;
  }

  @action
  public addDynamicOutputOption(
    treeBlockId: ReactText,
    blockType: TreeBlockType,
    option: LocalOutput
  ): void {
    option.optionId = uuidv4();
    option.value = this.prepareOutputValue(option.value, blockType);
    this.log.debug("new option", option);
    if (this.validateOutputOption(treeBlockId, option)) {
      if (
        this.outputOptions[treeBlockId].findIndex(
          o => o.value == option.value
        ) < 0
      ) {
        this.outputOptions[treeBlockId].push(option);
      } else {
        showErrorMessage("hns:tree:treeBlock:output:alreadyExists");
      }
    }
  }

  @action
  public deleteDynamicOutputOption(
    treeBlockId: ReactText,
    option: LocalOutput
  ): void {
    const outputIndex = this.outputOptions[treeBlockId].findIndex(
      o => o.value == option.value
    );
    this.outputOptions[treeBlockId].splice(outputIndex, 1);
  }

  @action
  public validateOutputOption(
    treeBlockId: ReactText,
    option: LocalOutput
  ): boolean {
    const block = this.findBlockById(treeBlockId);
    if (this.treeBlockConfigurations[block.type].outputCtxValidator) {
      // @ts-ignore
      const result = this.treeBlockConfigurations[
        block.type
      ].outputCtxValidator(option, this.outputOptions[treeBlockId]);
      return result;
    }
    return true;
  }

  public getOutputSchema(tBlockType: TreeBlockType): Schema {
    return (
      this.treeBlockConfigurations[tBlockType].outputFormSchema ||
      new Schema({
        fields: [],
        primaryKey: [],
        security: ""
      })
    );
  }

  public requiresForeignKeyRendering(outputType: OutputType): boolean {
    return [
      OutputType.LIST,
      OutputType.NETWORK_OPERATOR,
      OutputType.CALENDAR_DAY_TYPE,
      OutputType.AREA_GROUP
    ].some(item => item === outputType);
  }

  @action
  public initConfiguredOutputs(treeBlockId: ReactText): LocalOutput[] {
    if (!this.outputs[treeBlockId]) {
      this.outputs[treeBlockId] = [];
    }
    const currentBlock = this.findBlockById(treeBlockId);
    if (this.treeBlockStore.treeStructure.blocks) {
      const remoteOutputs = currentBlock.configuredOutputs;
      remoteOutputs?.forEach((output: RemoteOutput) => {
        const preparedOutput = {
          id: output.id,
          // prepare unique value for TrafficDistribution outputs
          value: this.prepareOutputValue(output.output, currentBlock.type),
          label: () => this.prepareOutputLabel(currentBlock, output),
          type: output.type,
          optionId: this.prepareOutputOptionId(
            currentBlock.type,
            output.id,
            output.output
          )
        };
        this.log.debug(
          "preparedOutput",
          currentBlock.type,
          preparedOutput,
          this.outputs[treeBlockId]
        );
        // special case of having the same output values
        if (
          currentBlock.type === TreeBlockType.TRAFFIC_DISTRIBUTION &&
          preparedOutput.type !== OutputType.DEFAULT
        ) {
          if (
            this.outputs[treeBlockId].findIndex(
              o => o.id == preparedOutput.id
            ) < 0
          ) {
            this.outputs[treeBlockId].push(preparedOutput);
          }
        } else {
          if (
            this.outputs[treeBlockId].findIndex(
              o => o.value == preparedOutput.value
            ) < 0
          ) {
            this.outputs[treeBlockId].push(preparedOutput);
          }
        }
      });
    }
    // add default outputs
    this.treeBlockConfigurations[currentBlock.type].outputOptions?.forEach(
      (option: LocalOutput) => {
        if (option.isAddedByDefault) {
          if (
            this.outputs[treeBlockId].findIndex(o => o.value == option.value) <
            0
          ) {
            this.outputs[treeBlockId].push(option);
          }
        }
      }
    );
    return this.outputs[treeBlockId];
  }

  @action
  private prepareOutputLabel(
    block: BlockObject,
    remoteOutput: RemoteOutput
  ): any {
    let finalLabel = remoteOutput.output;
    const option = this.treeBlockConfigurations[block.type].outputOptions?.find(
      (option: LocalOutput) => option.value == remoteOutput.output
    );
    if (option?.value == remoteOutput.output) {
      finalLabel = option?.label;
    }
    return finalLabel;
  }

  @action
  private prepareOutputOptionId(
    blockType: TreeBlockType,
    remoteId: ReactText,
    remoteValue: string
  ): ReactText {
    /* For dynamic case optionId is assigned as dataBase identifier */
    let finalOptionId = remoteId;
    /* Check if option is static */
    const staticOption = this.treeBlockConfigurations[
      blockType
    ].outputOptions?.find(
      (option: LocalOutput) => option.value === remoteValue
    );
    /* If it is static assign optionId from the dictionary */
    if (staticOption && staticOption.value == remoteValue) {
      // @ts-ignore
      finalOptionId = staticOption.optionId;
    }
    return finalOptionId;
  }

  @action
  public assign(treeBlockId: ReactText, checkedOutputs: LocalOutput[]): void {
    this.outputs[treeBlockId] = checkedOutputs;
    this.log.debug("assign newly checked outputs", this.outputs[treeBlockId]);
  }

  @action
  public setFormOutputType(outputType: OutputType | null): void {
    this.formOutputType = outputType;
  }

  @action
  public isFormOutputTypeOfType(outputType: OutputType): boolean {
    return this.formOutputType === outputType;
  }

  @action
  public updateOutputOption(
    blockType: TreeBlockType,
    treeBlockId: string,
    value: string,
    newId: string
  ) {
    this.log.debug(
      "updateOutputOption params",
      blockType,
      treeBlockId,
      value,
      newId
    );
    const index = this.outputOptions[treeBlockId].findIndex(
      option => option.value === value
    );
    const optionToUpdate = this.outputOptions[treeBlockId][index];
    this.log.debug("updateOutputOption optionToUpdate", optionToUpdate);
    this.log.debug("updateOutputOption outputs", this.outputs[treeBlockId]);
    this.log.debug(
      "updateOutputOption outputOptions",
      this.outputOptions[treeBlockId]
    );

    optionToUpdate.id = newId;
    optionToUpdate.optionId = this.prepareOutputOptionId(
      blockType,
      newId,
      value
    );
  }
}
